import React from 'react';
import { View } from 'react-native';

import modalManager from '../../../modules/modalManager';
import ModalAdapter from '../ModalAdapter';
import Janitor from '../../../modules/Janitor';

interface IUnmountModal {
  modal: { closeModal: () => Promise<any> }
}

type UnmountModal = Function & IUnmountModal;

/**
 * Tracks the open and closed state of a ModalAdapter component and supports opening and closing
 * when desired.
 */
export default class ManagedModal {
  /** Always set to `ModalTrigger`. This is needed to avoid circular dependencies */
  triggerType: React.ElementType;

  /** Renders the modal. See ui/ModalTrigger for more information */
  renderModal: (close: () => void) => void;

  /** Called when the trigger is activated by the user */
  onTriggerClick: () => void;

  /** Called when the modal opens */
  onModalOpen: () => void;

  /** Called when the modal closes, with the `result` passed to our `close` function */
  onModalClose: (result: any) => void;

  /** True if the modal is currently open */
  isOpen: boolean;

  /** Function to call to unmount the modal */
  unmountModal?: UnmountModal;

  /** Janitor handling maintenance of items */
  janitor: any;

  /** Props passed to the ModalHost wrapper */
  hostProps: any;

  constructor({
    triggerType,
    renderModal,
    onTriggerClick,
    onModalOpen,
    onModalClose,
    hostProps,
  }: {
    triggerType: React.ElementType;
    renderModal: (close: (result?: any) => void) => void;
    onTriggerClick?: () => void;
    onModalOpen?: () => void;
    onModalClose?: (result: any) => void;
    hostProps?: any;
  }) {
    this.triggerType = triggerType;
    this.renderModal = renderModal;
    this.onTriggerClick = onTriggerClick;
    this.onModalOpen = onModalOpen;
    this.onModalClose = onModalClose;

    this.isOpen = false;
    this.unmountModal = null;
    this.janitor = new Janitor();
    this.hostProps = hostProps;
  }

  /**
   * Clean up any lose ends
   */
  cleanup() {
    this.janitor.cleanup();
  }

  /**
   * Open the modal using information passed in the constructor.
   */
  open() {
    const {
      triggerType,
      onModalOpen,
      renderModal,
      hostProps,
    } = this;

    if (typeof this.onTriggerClick === 'function') {
      this.onTriggerClick();
    }

    if (this.isOpen || this.unmountModal) {
      return;
    }

    this.unmountModal = modalManager.openModal(ModalAdapter, {
      triggerType,
      renderTrigger: open => {
        // Open immediately if not already open, e.g. a re-render
        if (!this.isOpen) {
          this.isOpen = true;
          // Use timeout just in case we're coming from a render function
          // to prevent the react warning about setting state inside the render cycle.
          this.janitor.setTimeout(open);
        }
        // Show nothing in the actual view since this exists to create a modal in Angular
        return React.createElement(View);
      },
      renderModal,
      onModalOpen,
      onModalClose: result => this.close(result),
      hostProps,
    }) as any as UnmountModal;
  }

  /**
   * Close the modal, passing `result` to `onModalClose`
   * @param [result] A result of the modal operation. Can be anything.
   */
  close(result?: any) {
    const { onModalClose, unmountModal } = this;

    if (!unmountModal) {
      return;
    }

    const { modal } = unmountModal;

    this.unmountModal = null;
    modal.closeModal().then(() => {
      unmountModal();
      this.isOpen = false;
    });

    if (onModalClose) {
      onModalClose(result);
    }
  }
}
